import { supabase } from "../supabase/SupabaseClient";
import { createRow, deleteRow, updateRow } from "./supabaseRealtime";
import { claveDni, claveInterno } from "../shared/asignacion";
import { traerTodas } from "./paginar";

/* Qué tractor tiene asignado cada chofer: las consultas.
 *
 * La REGLA de cómo se lee (asignado vs habitual, quién gana, qué se propone)
 * vive en `shared/asignacion.js` y no acá. Esto es sólo el viaje a la base.
 *
 * ── SE TRAEN TODAS Y NO LAS PRIMERAS MIL ─────────────────────────────────
 *
 * `traerTodas` con `count: "exact"`, como manda `paginar.js`. Hoy la tabla
 * tiene pocas filas y un `.select()` pelado alcanzaría — pero el techo de
 * PostgREST no avisa cuando se cruza, y el día que haya 1.001 asignaciones la
 * pantalla mostraría 1.000 sin ninguna señal. Es exactamente el error que
 * `sinLimitesFalsos.test.js` existe para que no vuelva.
 *
 * ── LA CLAVE ES `dni`, NO `id` ───────────────────────────────────────────
 *
 * Por eso las escrituras pasan el cuarto parámetro de `createRow`/`updateRow`.
 * Sin él, PostgREST rechaza el `.eq("id", …)` sobre una tabla que no tiene esa
 * columna y la corrección no entra nunca — es lo que le pasó a
 * `chofer_documentos` y lo que dejó sus 224 filas sin autor.
 */

const CAMPOS = "dni,interno,desde,nota,usuario,usuario_nombre,actualizado_en";

export async function traerAsignaciones() {
  return traerTodas(() =>
    supabase
      .from("chofer_tractor")
      .select(CAMPOS, { count: "exact" })
      // `dni` es la clave primaria: un orden que no empata nunca, que es lo
      // que `paginar.js` exige para no repetir ni perder filas.
      .order("dni", { ascending: true }),
  );
}

/* La de una persona sola. `null` es «no tiene asignado», que NO es un error:
 * es el estado normal de casi todo el padrón mientras la tabla se llena. */
export async function asignacionDe(dni) {
  const k = claveDni(dni);
  if (!k) return null;
  const { data, error } = await supabase
    .from("chofer_tractor")
    .select(CAMPOS)
    .eq("dni", k)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

/* Los que tienen asignado un camión. Devuelve LISTA: 178 de los 307 tractores
 * que salieron en 180 días llevaron más de un chofer. */
export async function asignadosA(interno) {
  const k = claveInterno(interno);
  if (!k) return [];
  const { data, error } = await supabase
    .from("chofer_tractor")
    .select(CAMPOS)
    .eq("interno", k)
    .order("desde", { ascending: false });
  if (error) throw error;
  return data || [];
}

/* La tarjeta del tacógrafo de OTRO: el episodio abierto de este camión, o
 * `null`. Lo escribe el bot cuando el que salió por portería y el dueño de la
 * tarjeta puesta no son la misma persona; acá sólo se lee. */
export async function tarjetaAjenaDe(interno) {
  const k = claveInterno(interno);
  if (!k) return null;
  const { data, error } = await supabase
    .from("tacografo_tarjeta_ajena")
    .select("id,interno,dni_porteria,nombre_porteria,dni_tarjeta,nombre_tarjeta,visto_en")
    .eq("interno", k)
    .is("cerrado_en", null)
    .order("visto_en", { ascending: false })
    .limit(1);
  if (error) throw error;
  return (data || [])[0] || null;
}

/* Asignar o cambiar. Devuelve la fila guardada.
 *
 * SE MIRA ANTES SI YA TENÍA, y no se hace un `upsert` a ciegas: la pantalla
 * necesita poder avisar «Fulano hoy tiene el 700, ¿se lo cambiamos?» antes de
 * pisarlo. Un upsert silencioso movería a alguien de camión sin que quien
 * aprieta el botón se entere — y el historial lo registraría igual, pero
 * después.
 */
export async function asignar(dni, interno, { desde, nota } = {}) {
  const k = claveDni(dni);
  const i = claveInterno(interno);
  if (!k) throw new Error("Sin DNI no se puede asignar.");
  if (!i) throw new Error("Sin interno no se puede asignar.");

  const datos = { interno: i };
  if (desde) datos.desde = desde;
  // `nota` viaja incluso vacía: es la forma de BORRAR una nota vieja. Sin
  // esto, «mientras Pérez está de licencia» quedaría pegada para siempre.
  if (nota !== undefined) datos.nota = nota || null;

  const previa = await asignacionDe(k);
  if (previa) return updateRow("chofer_tractor", k, datos, "dni");
  return createRow("chofer_tractor", k, datos, "dni");
}

/* Desasignar es SACAR LA FILA, y no dejarla con el interno en blanco: una
 * asignación anulada con el camión vacío se leería como «tiene camión, no
 * sabemos cuál». Que lo tuvo no se pierde — el trigger de la base lo dejó
 * escrito en `chofer_tractor_cambio` con la fecha y con quién lo sacó. */
export async function desasignar(dni) {
  const k = claveDni(dni);
  if (!k) return;
  await deleteRow("chofer_tractor", k, "dni");
}

/* El historial, para contestar «¿quién le sacó el camión a Fulano?». Se puede
 * pedir por persona o por camión; sin ninguno de los dos no devuelve el
 * historial entero, que sería traer toda la tabla para dibujar un panel. */
export async function historial({ dni, interno } = {}, tope = 50) {
  const k = claveDni(dni);
  const i = claveInterno(interno);
  if (!k && !i) return [];
  let q = supabase
    .from("chofer_tractor_cambio")
    .select("id,dni,interno_antes,interno_despues,usuario,usuario_nombre,cuando")
    .order("cuando", { ascending: false })
    .limit(tope);
  if (k) q = q.eq("dni", k);
  if (i) q = q.or(`interno_antes.eq.${i},interno_despues.eq.${i}`);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/* ── La tendencia, que es el OTRO dato ────────────────────────────────────
 *
 * `tractor_habitual` cuenta las salidas de portería y devuelve con qué unidad
 * suele salir cada uno, con su porcentaje y su racha. No es una asignación y
 * no se guarda en `chofer_tractor`: es lo que la pantalla muestra AL LADO,
 * para que se vea cuándo la asignación quedó vieja.
 *
 * SÓLO LO CONFIABLE (`confiable`, que del lado de la base es ≥90% con al menos
 * 3 salidas). El umbral está medido para esta dirección —83% de acierto arriba
 * de 90, 64% entre 80 y 89— y dejar pasar el flojo para que decida el que
 * llama es cómo un umbral se termina ignorando en el segundo lugar que lo use.
 * Es la misma regla que ya aplica `ubicacion_chofer.habitual()` en FlotaBot.
 *
 * `null` es «no pude consultar» y no «no tiene»: con un objeto vacío, una
 * caída de la RPC se leería como que nadie tiene tendencia.
 */
export async function habitualDe(dni) {
  const k = claveDni(dni);
  if (!k) return null;
  const { data, error } = await supabase.rpc("tractor_habitual", { p_dni: k });
  if (error) throw error;
  const f = (data || [])[0] || null;
  return f && f.confiable ? f : null;
}

/* Los que SUELEN salir con este camión. Es la vuelta de la anterior y no hay
 * RPC para ese sentido: se trae la tabla entera —una sola pasada, 359 ms
 * medidos— y se filtra acá. Una llamada por chofer serían 379 iteraciones.
 *
 * Devuelve `[]` cuando no hay ninguno, que es distinto de que la consulta
 * falle: eso se propaga como error y el bloque lo dibuja como «no se pudo». */
export async function habitualesDelTractor(interno) {
  const k = claveInterno(interno);
  if (!k) return [];
  const { data, error } = await supabase.rpc("tractores_habituales", {});
  if (error) throw error;
  return (data || [])
    .filter((f) => f?.confiable && claveInterno(f.tractor) === k)
    .sort((a, b) => (b?.salidas || 0) - (a?.salidas || 0));
}
